export const dynamic = 'force-dynamic';
import { FC, useMemo } from 'react';
import { Text, View } from '@react-pdf/renderer';

import { AssetItem } from '@/entities/interwills';
import { BaseAssetTypesList } from '@/shared/lib/forms/asset-form/commodity-asset-token';
import { BasicAssetList } from '@/shared/lib/forms/asset-form/exchange-options';
import { AssetCategory } from '@/shared/lib/sj-orm/models/asset.dto';
import { JewelryType, PreciousGemstonesType } from '@/shared/lib/sj-orm/models/assets/enums';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { usePrivateDocuments } from './hooks/usePrivateDocuments';
import { buildAssetAddress } from './helpers';
import { useBank, useWithPlaceholder } from './hooks';

type AssetDetailsProps = {
  asset: AssetItem;
  isDraft?: boolean;
};

export const AssetDetails: FC<AssetDetailsProps> = ({ asset, isDraft }) => {
  const { t } = useTranslation('widgets/pdfs', {
    keyPrefix: 'lastWill',
  });
  const { t: ta } = useTranslation(['assets', 'forms_assets']);
  const withPlaceholder = useWithPlaceholder();
  const { getBank } = useBank();
  const { getDocument } = usePrivateDocuments();

  const withPlaceholderTypeCheck = (assetItem: AssetItem, key: string, placeholder: string) => {
    let str = assetItem[key as keyof AssetItem] as string | Date;

    if (str instanceof Date) {
      str = dates.formatMask(str, { month: 'long', day: '2-digit', year: 'numeric' });
    }

    return withPlaceholder(str ?? undefined, placeholder);
  };

  const withDatePlaceholder = (assetItem: AssetItem, key: string, placeholder: string) => {
    return withPlaceholder(
      key in assetItem ? dates.formatLong(assetItem[key as keyof AssetItem] as string) : undefined,
      placeholder,
    );
  };

  const isKeyOf = <T extends object>(key: string | number | symbol, obj: T): key is keyof T => {
    return key in obj;
  };

  const displayOptionalFields = (...keys: string[]) => {
    return isDraft || keys.every((key) => isKeyOf(key, asset) && asset[key]);
  };

  return useMemo(() => {
    switch (asset.category) {
      case AssetCategory.LAND:
        return (
          <View>
            <Text>
              {t('assetsBlock.address')}: {buildAssetAddress(asset)},{' '}
              {t('assetsBlock.landPlotNumber')}:{' '}
              {withPlaceholderTypeCheck(asset, 'numberName', 'name')}{' '}
              {withPlaceholderTypeCheck(asset, 'number', 'number')}.
            </Text>
          </View>
        );
      case AssetCategory.RESIDENTIAL_REAL_ESTATE:
        return (
          <View>
            <Text>
              {t('assetsBlock.address')}: {buildAssetAddress(asset)},{' '}
              {t('assetsBlock.realEstateNumber')}:{' '}
              {withPlaceholderTypeCheck(asset, 'residentialRealEstateNumberName', 'name')}{' '}
              {withPlaceholderTypeCheck(asset, 'residentialRealEstateNumber', 'number')}.
            </Text>
          </View>
        );
      case AssetCategory.COMMERCIAL_REAL_ESTATE:
        return (
          <View>
            <Text>
              {t('assetsBlock.address')}: {buildAssetAddress(asset)},{' '}
              {t('assetsBlock.realEstateNumber')}:{' '}
              {withPlaceholderTypeCheck(asset, 'commercialRealEstateNumber', 'number')}{' '}
              {withPlaceholderTypeCheck(asset, 'commercialRealEstateNumberName', 'name')}.
            </Text>
          </View>
        );
      case AssetCategory.METAL_ACCOUNTS:
        return (
          <>
            <View>
              <Text>
                {t('assetsBlock.allFundsOwnedBy')} {withPlaceholderTypeCheck(asset, 'iban', 'iban')}{' '}
                {t('assetsBlock.at')}{' '}
                {withPlaceholder(getBank(asset.holder)?.nickname, 'branchName')}.
              </Text>
            </View>
            <View>
              <Text>
                {t('assetsBlock.bankAddress')}:{' '}
                {withPlaceholder(getBank(asset.holder)?.bankAddress, 'bankAddress')}.
              </Text>
            </View>
          </>
        );
      case AssetCategory.DEBIT_ACCOUNTS:
        return (
          <>
            <View>
              <Text>
                {t('assetsBlock.allFundsOwnedBy')} {withPlaceholderTypeCheck(asset, 'iban', 'iban')}{' '}
                {t('assetsBlock.at')} {withPlaceholder(getBank(asset.bank)?.nickname, 'branchName')}
                .
              </Text>
            </View>
            <View>
              <Text>
                {t('assetsBlock.bankAddress')}:{' '}
                {withPlaceholder(getBank(asset.bank)?.bankAddress, 'bankAddress')}.
              </Text>
            </View>
          </>
        );
      case AssetCategory.BANK_CELLS:
        return (
          <>
            <View>
              <Text>
                {t('assetsBlock.allFundsOwnedByBox')}{' '}
                {withPlaceholderTypeCheck(asset, 'depositBoxNumber', 'number')}{' '}
                {t('assetsBlock.at')} {withPlaceholder(getBank(asset.bank)?.nickname, 'branchName')}
                .
              </Text>
            </View>
            <View>
              <Text>
                {t('assetsBlock.holderAddress')}:{' '}
                {withPlaceholder(getBank(asset.bank)?.bankAddress, 'bankAddress')}.
              </Text>
            </View>
          </>
        );
      case AssetCategory.SECURITIES_AND_STOCKS:
      case AssetCategory.BONDS:
        return (
          <View>
            <Text>
              {t('assetsBlock.isin')} {withPlaceholderTypeCheck(asset, 'isin', 'isin')}
              {displayOptionalFields('brokerageAccountNumber', 'holder') && (
                <>
                  , {t('assetsBlock.heldInAccountNumber')}{' '}
                  {withPlaceholderTypeCheck(asset, 'brokerageAccountNumber', 'accountNumber')}{' '}
                  {t('assetsBlock.at')}{' '}
                  {withPlaceholder(getBank(asset.holder!)?.nickname, 'branchName')},{' '}
                  {t('assetsBlock.address').toLowerCase()}:{' '}
                  {withPlaceholder(getBank(asset.holder!)?.bankAddress, 'bankAddress')}
                </>
              )}
              .{' '}
              {displayOptionalFields('depositaryName', 'depositaryAccountNumber') && (
                <>
                  {t('assetsBlock.depositaryName')}:{' '}
                  {withPlaceholderTypeCheck(asset, 'depositaryName', 'depoName')}.{' '}
                  {t('assetsBlock.depoAccNumber')}:{' '}
                  {withPlaceholderTypeCheck(asset, 'depositaryAccountNumber', 'depoAccNumber')}.
                </>
              )}
            </Text>
          </View>
        );
      case AssetCategory.FUTURES:
        return (
          <View>
            <Text>
              {t('assetsBlock.uniqueId')}: {withPlaceholderTypeCheck(asset, 'ticker', 'ticker')}
              {displayOptionalFields('basicAssetName') && (
                <>
                  , {t('assetsBlock.underlyingName')}:{' '}
                  {withPlaceholderTypeCheck(asset, 'basicAssetName', 'assetName')}
                </>
              )}
              {displayOptionalFields('expirationDate') && (
                <>
                  , {t('assetsBlock.expirationDate')}:{' '}
                  {withDatePlaceholder(asset, 'expirationDate', 'expirationDate')}
                </>
              )}
              {displayOptionalFields('brokerAccountNumber', 'broker') && (
                <>
                  , {t('assetsBlock.heldInAccountNumber')}
                  {'  '}
                  {withPlaceholderTypeCheck(asset, 'brokerAccountNumber', 'accountNumber')}{' '}
                  {t('assetsBlock.at')}{' '}
                  {withPlaceholder(getBank(asset.broker)?.nickname, 'branchName')},{' '}
                  {t('assetsBlock.address').toLowerCase()}:{' '}
                  {withPlaceholder(getBank(asset.broker)?.bankAddress, 'branchAddress')}
                </>
              )}
              .
            </Text>
          </View>
        );
      case AssetCategory.EXCHANGE_OPTIONS:
        return (
          <View>
            <Text>
              {displayOptionalFields('type') && (
                <>
                  {t('assetsBlock.optionsType')}:{' '}
                  {withPlaceholder(
                    asset.type
                      ? ta(`forms_assets:exchangeOptions.enums.optionType.${asset.type}`)
                      : undefined,
                    'optionsType',
                  )}
                  ,{' '}
                </>
              )}
              {t('assetsBlock.uniqueId').toLowerCase()}:{' '}
              {withPlaceholderTypeCheck(asset, 'ticker', 'ticker')}
              {displayOptionalFields('expirationDate') && (
                <>
                  , {t('assetsBlock.expirationDate')}:{' '}
                  {withDatePlaceholder(asset, 'expirationDate', 'expirationDate')}
                </>
              )}
              {displayOptionalFields('brokerAccountNumber', 'broker') && (
                <>
                  , {t('assetsBlock.heldInAccountNumber')}{' '}
                  {withPlaceholderTypeCheck(asset, 'brokerAccountNumber', 'accountNumber')}{' '}
                  {t('assetsBlock.at')}{' '}
                  {withPlaceholder(getBank(asset.broker!)?.nickname, 'branchName')},{' '}
                  {t('assetsBlock.address').toLowerCase()}:{' '}
                  {withPlaceholder(getBank(asset.broker!)?.bankAddress, 'branchAddress')}
                </>
              )}
              .
            </Text>
          </View>
        );
      case AssetCategory.MY_FUND:
      case AssetCategory.PUBLIC_FUND:
        return (
          <View>
            <Text>
              {t('assetsBlock.fundText')}{' '}
              {withPlaceholderTypeCheck(asset, 'fullLegalName', 'fullName')}
              {displayOptionalFields('lei') && (
                <>
                  , {t('assetsBlock.regUnderNumber')}{' '}
                  {withPlaceholderTypeCheck(asset, 'lei', 'lei')}
                </>
              )}{' '}
              {t('assetsBlock.atAddress')} {withPlaceholder(buildAssetAddress(asset), 'address')}
              {displayOptionalFields('tax') && (
                <>
                  , {t('assetsBlock.taxNumber')}{' '}
                  {withPlaceholderTypeCheck(asset, 'taxNumber', 'tax')}
                </>
              )}
              .
            </Text>
          </View>
        );
      case AssetCategory.BROKERAGE_ACCOUNT:
        return (
          <View>
            <Text>
              {t('assetsBlock.heldInAcc')} {withPlaceholderTypeCheck(asset, 'iban', 'iban')}
              {displayOptionalFields('holder') && (
                <>
                  {' '}
                  {t('assetsBlock.at')}{' '}
                  {withPlaceholder(getBank(asset.holder)?.nickname, 'branchName')},{' '}
                  {t('assetsBlock.address').toLowerCase()}:{' '}
                  {withPlaceholder(getBank(asset.holder)?.bankAddress, 'branchName')}
                </>
              )}
              , {t('assetsBlock.includingAssets')}
            </Text>
          </View>
        );
      case AssetCategory.LEGAL_ENTITIES:
        return (
          <View>
            <Text>
              {t('assetsBlock.rightOfManagement')}{' '}
              {withPlaceholderTypeCheck(asset, 'legalName', 'fullName')}
              {displayOptionalFields('structure') && (
                <>
                  , {t('assetsBlock.lei')} {withPlaceholderTypeCheck(asset, 'structure', 'lei')},
                </>
              )}
              {displayOptionalFields('tax') && (
                <>
                  {' '}
                  {t('assetsBlock.taxNumber')} {withPlaceholderTypeCheck(asset, 'taxNumber', 'tax')}
                  ,
                </>
              )}
              {displayOptionalFields('dateOfIncorporation') && (
                <>
                  {' '}
                  {t('assetsBlock.dateOfIncorporation')}:{' '}
                  {withDatePlaceholder(asset, 'dateOfIncorporation', 'date')},
                </>
              )}{' '}
              {t('assetsBlock.address')}: {withPlaceholder(buildAssetAddress(asset), 'address')}.
            </Text>
          </View>
        );
      case AssetCategory.OVER_THE_COUNTER_OPTIONS:
        return (
          <View>
            <Text>
              {displayOptionalFields('type') && (
                <>
                  {t('assetsBlock.optionsType')}:{' '}
                  {withPlaceholder(
                    asset.type ? ta(`forms_assets:otcOptions.enums.type.${asset.type}`) : undefined,
                    'optionsType',
                  )}
                  .{' '}
                </>
              )}
              <Text style={{ textTransform: 'upperfirst' }}>{t('assetsBlock.underlyingName')}</Text>
              : {withPlaceholderTypeCheck(asset, 'basicAssetName', 'assetName')}.{' '}
              {t('assetsBlock.underlyingAssetHolder')}:{' '}
              {withPlaceholderTypeCheck(asset, 'basicAssetHolder', 'assetHolder')}.
              {'counterpartyType' in asset && asset.counterpartyType === 'person' ? (
                displayOptionalFields('personFullName') ? (
                  <>
                    {' '}
                    {t('assetsBlock.otherSideOfContract')}{' '}
                    {withPlaceholderTypeCheck(asset, 'personFullName', 'fullName')}.
                  </>
                ) : null
              ) : displayOptionalFields('legalEntityName') ? (
                <>
                  {' '}
                  {t('assetsBlock.otherSideOfContract')}{' '}
                  {withPlaceholderTypeCheck(asset, 'legalEntityName', 'fullName')}.
                </>
              ) : null}
            </Text>
          </View>
        );
      case AssetCategory.FORWARDS: {
        const assetType =
          'basicAssetType' in asset
            ? BasicAssetList.find((item) => item.key === asset.basicAssetType)?.label
            : undefined;

        return (
          <View>
            <Text>
              {t('assetsBlock.forwardType')}:{' '}
              {withPlaceholder(assetType ? ta(assetType).toLowerCase() : undefined, 'type')}.{' '}
              {t('assetsBlock.assetType')}:{' '}
              {withPlaceholder(assetType ? ta(assetType) : undefined, 'type')}.
              {'counterpartyType' in asset && asset.counterpartyType === 'person' ? (
                displayOptionalFields('personFullName') ? (
                  <>
                    {' '}
                    {t('assetsBlock.otherSideOfContract')}{' '}
                    {withPlaceholderTypeCheck(asset, 'personFullName', 'fullName')}.
                  </>
                ) : null
              ) : displayOptionalFields('legalEntityName') ? (
                <>
                  {' '}
                  {t('assetsBlock.otherSideOfContract')}{' '}
                  {withPlaceholderTypeCheck(asset, 'legalEntityName', 'fullName')}.
                </>
              ) : null}
            </Text>
          </View>
        );
      }
      case AssetCategory.CENTRALIZED_WALLET:
        return (
          <View>
            <Text>
              {t('assetsBlock.helpInAccNum')}{' '}
              {withPlaceholderTypeCheck(asset, 'accountNumber', 'accountNumber')}{' '}
              {t('assetsBlock.at')} {withPlaceholderTypeCheck(asset, 'holder', 'holder')}{' '}
              {t('assetsBlock.notNamed')}
            </Text>
          </View>
        );
      case AssetCategory.DECENTRALIZED_WALLET:
        return (
          <View>
            <Text>
              {t('assetsBlock.heldInWalletType')}{' '}
              {withPlaceholderTypeCheck(asset, 'walletType', 'type')}{' '}
              {t('assetsBlock.officialWalletName')}{' '}
              {withPlaceholderTypeCheck(asset, 'officialWalletName', 'name')}.
            </Text>
          </View>
        );
      case AssetCategory.JEWELLERY: {
        const jewelryType =
          asset.type === JewelryType.other && 'otherType' in asset
            ? (asset.otherType as string)
            : ta(`forms_assets:jewelery.enums.type.${asset.type}`);

        return (
          <View>
            <Text>
              {t('assetsBlock.jewelryType')}: {withPlaceholder(jewelryType, 'type')}.{' '}
              {t('assetsBlock.serialNumber')}:{' '}
              {withPlaceholderTypeCheck(asset, 'serialNumber', 'number')}.{' '}
              {displayOptionalFields('receipt') && (
                <>
                  {t('assetsBlock.purchaseReceipt')}{' '}
                  {withPlaceholder(getDocument(asset.receipt)?.number, 'number')}{' '}
                  {t('assetsBlock.dated')}{' '}
                  {withPlaceholder(
                    dates.formatLongDate(String(getDocument(asset.receipt)?.startDate ?? '')),
                    'date',
                  )}
                  .{' '}
                </>
              )}
              {displayOptionalFields('storageLocation') && (
                <>
                  {t('assetsBlock.storageLocation')}:{' '}
                  {withPlaceholderTypeCheck(asset, 'storageLocation', 'location')}.
                </>
              )}
            </Text>
          </View>
        );
      }
      case AssetCategory.PRECIOUS_STONES: {
        const receipt = getDocument(asset.purchaseReceipt);
        const gemstoneType =
          asset.type === PreciousGemstonesType.other && 'otherType' in asset
            ? (asset.otherType as string)
            : ta(`forms_assets:gemstones.enums.type.${asset.type}`);

        return (
          <View>
            <Text>
              {t('assetsBlock.gemType')}: {withPlaceholder(gemstoneType, 'type')}.{' '}
              {t('assetsBlock.serialNumber')}:{' '}
              {withPlaceholderTypeCheck(asset, 'serialNumber', 'number')}.{' '}
              {displayOptionalFields('receipNumber') && (
                <>
                  {t('assetsBlock.purchaseReceipt')} {withPlaceholder(receipt?.number, 'number')}{' '}
                </>
              )}
              {displayOptionalFields('receipDate') && (
                <>
                  {t('assetsBlock.dated')}{' '}
                  {withPlaceholder(dates.formatLongDate(String(receipt?.startDate ?? '')), 'date')}.{' '}
                </>
              )}
              {displayOptionalFields('storageLocation') && (
                <>
                  {t('assetsBlock.storageLocation')}:{' '}
                  {withPlaceholderTypeCheck(asset, 'storageLocation', 'location')}.
                </>
              )}
            </Text>
          </View>
        );
      }
      case AssetCategory.COMMODITY_ASSET_TOKENS: {
        const type =
          'baseAssetType' in asset
            ? BaseAssetTypesList.find((item) => item.key === asset.baseAssetType)
            : null;

        return (
          <View>
            <Text>
              {t('assetsBlock.uniqueId')}:{' '}
              {withPlaceholderTypeCheck(asset, 'identifierName', 'identifierName')}{' '}
              {withPlaceholderTypeCheck(asset, 'identifierNumber', 'identifierNumber')}.{' '}
              {t('assetsBlock.underlyingAssetType')}:{' '}
              {withPlaceholder(type?.label ? ta(type.label) : undefined, 'type')}.{' '}
              {t('assetsBlock.issuerLegalName')}:{' '}
              {withPlaceholderTypeCheck(asset, 'issuerLegalName', 'issuerLegalName')}.{' '}
              {t('assetsBlock.issuerLocation')}:{' '}
              {withPlaceholderTypeCheck(asset, 'issuerLocation', 'issuerLocation')}.
            </Text>
          </View>
        );
      }
      default:
        return null;
    }
  }, [asset]);
};
